import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../../components/commonComponent/CommanTable';
import CommanFilters from '../../components/commonComponent/CommanFilters';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import { FaPencilAlt, FaTrashAlt, FaEye } from 'react-icons/fa';
import { hasPermission } from "../../utils/permissionHelper";

const tabConfig = [
  {
    key: 'client',
    label: 'Client',
    addLabel: 'Add New Client',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Customer name' },
      { key: 'startDate', label: 'Start Date' },
      { key: 'endDate', label: 'End Date' },
      { key: 'lastupdate', label: 'Last update' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Client Name', type: 'text', placeholder: 'Enter Client Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    sampleRow: { name: 'Default Client' },
  },
  {
    key: 'state',
    label: 'State',
    addLabel: 'Add New State',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'offerName', label: 'Offer name' },
      { key: 'name', label: 'State name' },
      { key: 'startDate', label: 'Start Date' },
      { key: 'endDate', label: 'End Date' },
      { key: 'lastupdate', label: 'Last update' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'State Name', type: 'text', placeholder: 'Enter State Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    sampleRow: { name: 'Default State' },
  },
  {
    key: 'category',
    label: 'Category',
    addLabel: 'Add New Category',
    columns: [
      { key: 'srNo', label: 'Sr.no:' },
      { key: 'name', label: 'Category name' },
      { key: 'startDate', label: 'Start Date' },
      { key: 'endDate', label: 'End Date' },
      { key: 'lastupdate', label: 'Last update' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Action' },
    ],
    modalFields: [
      { name: 'name', label: 'Category Name', type: 'text', placeholder: 'Enter Category Name' },
    ],
    initialValues: { name: '' },
    requiredFields: ['name'],
    sampleRow: { name: 'Default Category' },
  },
];

const apiMap = {
  client: '/admin/api/priceClient',
  category: '/admin/api/priceCategory',
  state: '/admin/api/priceState',
};

function ItemManage() {
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('price_active_tab') || 'client');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ startDate: '', endDate: '', client: '', state: '', userCategory: '' });
  const [filterDropdowns, setFilterDropdowns] = useState({ clients: [], states: [], userCategories: [] });
  const [showFilters, setShowFilters] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDefaultLoading, setIsDefaultLoading] = useState(false);
  const [data, setData] = useState({});
  const [user, setUser] = useState(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) setUser(JSON.parse(stored));
    } catch (e) {
      // ignore
    }
  }, []);

  const canViewPrice = hasPermission(user, 'Price List', 'View');
  const canCreatePrice = hasPermission(user, 'Price List', 'Create');
  const canEditPrice = hasPermission(user, 'Price List', 'Edit');
  const canDeletePrice = hasPermission(user, 'Price List', 'Delete');

  const navigate = useNavigate();
  const getOptionVal = (it) => (it && (it._id ?? it.id ?? it.value ?? it.valueId ?? it.key)) || '';
  const getOptionLabel = (it) => {
    if (!it) return '';
    if (it.firstName) return `${it.firstName} ${it.lastName || ''}`.trim();
    return it.name || it.stateName || it.label || it.email || it.title || '';
  };
  const buildOptions = (list) => (Array.isArray(list) ? list.map(it => ({ value: getOptionVal(it), label: getOptionLabel(it) })) : []);
  async function handleDefault() {
    setIsDefaultLoading(true);
    try {
      await axios.post('/admin/api/default', {
        type: 'unit',
      });
      fetchTabData('unit');
    } catch (err) {
    } finally {
      setIsDefaultLoading(false);
    }
  }

  useEffect(() => {
    fetchTabData(activeTab);
    // eslint-disable-next-line
  }, [activeTab]);

  // Clear stored tab after first render (we used it to restore after create/save)
  useEffect(() => {
    try { sessionStorage.removeItem('price_active_tab'); } catch (e) {}
    // run only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load dropdowns for filters
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [usersRes, statesRes, catsRes] = await Promise.all([
          axios.get('/api/user'),
          axios.get('/admin/api/state'),
          axios.get('/admin/api/userCategory'),
        ]);

        const extractArray = (res) => {
          const data = res?.data ?? res;
          if (Array.isArray(data)) return data;
          if (Array.isArray(res?.data?.data)) return res.data.data;
          if (Array.isArray(res?.data?.data?.users)) return res.data.data.users;
          if (Array.isArray(res?.data?.users)) return res.data.users;
          if (Array.isArray(res?.data?.states)) return res.data.states;
          if (Array.isArray(res?.data?.userCategories)) return res.data.userCategories;
          if (data && typeof data === 'object') {
            const firstArray = Object.values(data).find(v => Array.isArray(v));
            if (Array.isArray(firstArray)) return firstArray;
          }
          return [];
        };

        const clients = extractArray(usersRes);
        const states = extractArray(statesRes);
        const userCategories = extractArray(catsRes);
        if (!mounted) return;
        setFilterDropdowns({ clients, states, userCategories });
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  

  async function fetchTabData(tabKey, params = {}) {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (params.startDate) qp.append('startDate', params.startDate);
      if (params.endDate) qp.append('endDate', params.endDate);
      if (params.client) qp.append('clientName', params.client);
      if (params.state) qp.append('state', params.state);
      if (params.userCategory) qp.append('userCategory', params.userCategory);
      const qs = qp.toString();
      const url = qs ? `${apiMap[tabKey]}?${qs}` : apiMap[tabKey];
      const res = await axios.get(url);
      let entries = res.data?.priceStates || res.data?.priceCategories || res.data?.priceClients || [];

      let items = [];
      const formatDateOnly = (d) => {
        if (!d) return '';
        try {
          const dt = new Date(d);
          if (isNaN(dt.getTime())) return '';
          const dd = String(dt.getDate()).padStart(2, '0');
          const mm = String(dt.getMonth() + 1).padStart(2, '0');
          const yyyy = dt.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        } catch (e) {
          return '';
        }
      };
      const formatDateShort = (d) => {
        if (!d) return '';
        try {
          const dt = new Date(d);
          if (isNaN(dt.getTime())) return '';
          const dd = String(dt.getDate()).padStart(2, '0');
          const mm = String(dt.getMonth() + 1).padStart(2, '0');
          const yyyy = String(dt.getFullYear());
          return `${dd}/${mm}/${yyyy.slice(-2)}`;
        } catch (e) {
          return '';
        }
      };
      const normalizePriceFromItem = (it) => {
        if (!it) return '';
        if (it.salesPrice) return it.salesPrice;
        if (Array.isArray(it.stateRate) && it.stateRate[0]?.price) return it.stateRate[0].price;
        return it.price || it.rate || '';
      };

      switch (tabKey) {
        case 'client':
          items = (Array.isArray(entries) ? entries : [entries]).map((entry, idx) => {
            const client = entry.clientName || entry.client || entry.user || {};
            const name = client.firstName ? `${client.firstName} ${client.lastName || ''}`.trim() : (client.name || client.email || '');
            const firstItem = Array.isArray(entry.items) && entry.items[0] ? entry.items[0] : null;
            const rawStatus = (entry.Status || entry.status || '');
            const s = String(rawStatus || '').trim();
            const key = s.toLowerCase();
            const STATUS_STYLE = key === 'active'
              ? { bg: '#DCF4E9', color: '#12B76A' }
              : (key === 'expired' ? { bg: '#FBE4E4', color: '#E14B4B' } : { bg: '#DFE6EB', color: '#000' });
            return {
              srNo: idx + 1,
              _id: entry._id,
              name,
              startDate: formatDateOnly(entry.startDate),
              endDate: formatDateOnly(entry.endDate),
              lastupdate: formatDateShort(entry.updatedDate || entry.updatedAt || entry.updatedAt),
              status: (
                  <span style={{ background: STATUS_STYLE.bg, color: STATUS_STYLE.color, padding: '4px 8px', borderRadius: 12, fontSize: '0.85rem' }}>
                    {s || '-'}
                  </span>
                ),
              };
            });
          break;
          case 'state':
          items = (Array.isArray(entries) ? entries : [entries]).map((entry, idx) => {
            const st = entry.state || entry.stateName || {};
            const stateName = st.name || '';
            const offer = entry.offerName || entry.offer || '';
            const displayName = stateName || offer;
            const rawStatus = (entry.Status || entry.status || '');
            const s = String(rawStatus || '').trim();
            const key = s.toLowerCase();
            const STATUS_STYLE = key === 'active'
              ? { bg: '#DCF4E9', color: '#12B76A' }
              : (key === 'expired' ? { bg: '#FBE4E4', color: '#E14B4B' } : { bg: '#DFE6EB', color: '#000' });
            return {
              srNo: idx + 1,
              _id: entry._id,
              offerName: offer,
              name: displayName,
              startDate: formatDateOnly(entry.startDate),
              endDate: formatDateOnly(entry.endDate),
              lastupdate: formatDateShort(entry.updatedDate || entry.updatedAt || entry.updatedAt),
              status: (
                  <span style={{ background: STATUS_STYLE.bg, color: STATUS_STYLE.color, padding: '4px 8px', borderRadius: 12, fontSize: '0.85rem' }}>
                    {s || '-'}
                  </span>
                ),
              };
            });
          break;
        case 'category':
          items = (Array.isArray(entries) ? entries : [entries]).map((entry, idx) => {
            const cat = entry.userCategory || entry.category || {};
            const name = cat.name || entry.offerName || '';
            const firstItem = Array.isArray(entry.items) && entry.items[0] ? entry.items[0] : null;
            const rawStatus = (entry.Status || entry.status || '');
            const s = String(rawStatus || '').trim();
            const key = s.toLowerCase();
            const STATUS_STYLE = key === 'active'
              ? { bg: '#DCF4E9', color: '#12B76A' }
              : (key === 'expired' ? { bg: '#FBE4E4', color: '#E14B4B' } : { bg: '#DFE6EB', color: '#000' });
            return {
              srNo: idx + 1,
              _id: entry._id,
              name,
              startDate: formatDateOnly(entry.startDate),
              endDate: formatDateOnly(entry.endDate),
              lastupdate: formatDateShort(entry.updatedDate || entry.updatedAt || entry.updatedAt),
              status: (
                <span style={{ background: STATUS_STYLE.bg, color: STATUS_STYLE.color, padding: '4px 8px', borderRadius: 12, fontSize: '0.85rem' }}>
                  {s || '-'}
                </span>
              ),
            };
          });
          break;
        default:
          items = [];
      }

      setData((prev) => ({
        ...prev,
        [tabKey]: items.map((item, idx) => ({
          srNo: idx + 1,
          ...item,
        })),
      }));
    } catch (err) {
      setData((prev) => ({ ...prev, [tabKey]: [] }));
    } finally {
      setLoading(false);
    }
  }

  const currentTab = tabConfig.find((tab) => tab.key === activeTab);

  if (!currentTab) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger" role="alert">
          Invalid tab configuration. Please check the tab settings.
        </div>
      </div>
    );
  }

  const iconHoverStyle = {
    transition: 'color 0.2s',
    cursor: 'pointer',
  };

  const rows = (data[activeTab] || [])
    .filter((row) => ((row.name || row.offerName) || '').toLowerCase().includes(searchTerm.toLowerCase()))
    .map((row, idx) => ({
      ...row,
      actions: (
        <div>
          <span style={iconHoverStyle}>
            <button
              className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
              title="View"
              onClick={() => {
                if (activeTab === 'client') return navigate(`/admin/clientOffer/view/${row._id}`);
                if (activeTab === 'state') return navigate(`/admin/state/view/${row._id}`);
                if (activeTab === 'category') return navigate(`/admin/category/view/${row._id}`);
                return navigate(`/admin/${activeTab}Offer/view/${row._id}`);
              }}
              style={{ background: 'none', padding: 0, border: 'none' }}
            >
              <FaEye size={14} />
            </button>
          </span>
          <span style={iconHoverStyle}>
            <button
              className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
              title="Edit"
              onClick={() => {
                if (activeTab === 'client') return navigate(`/admin/clientOffer/edit/${row._id}`);
                if (activeTab === 'state') return navigate(`/admin/stateOffer/edit/${row._id}`);
                if (activeTab === 'category') return navigate(`/admin/categoryOffer/edit/${row._id}`);
                return navigate(`/admin/${activeTab}/edit/${row._id}`);
              }}
              style={{ background: 'none', padding: 0, border: 'none' }}
            >
              <FaPencilAlt size={14} />
            </button>
          </span>
          <span style={iconHoverStyle}>
            <button
              className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0 text-danger"
              title="Delete"
              onClick={() => handleDelete(idx)}
              style={{ background: 'none', padding: 0, border: 'none' }}
            >
              <FaTrashAlt size={14} />
            </button>
          </span>
        </div>
      ),
    }));

  function handleAdd() {
    // store current tab so after add/save we can return to same tab
    try { sessionStorage.setItem('price_active_tab', activeTab); } catch (e) {}
    if (activeTab === 'client') return navigate('/admin/clientOffer');
    if (activeTab === 'state') return navigate('/admin/stateOffer');
    if (activeTab === 'category') return navigate('/admin/categoryOffer');
    return null;
  }

  async function handleDelete(idx) {
    const item = data[activeTab]?.[idx];
    if (!item || !item._id) return;
    try {
      const res = await confirmDelete(`Are you sure you want to delete ${item.name || item.gstName || ''}?`);
      if (!res.isConfirmed) return;

      await axios.delete(`${apiMap[activeTab]}/${item._id}`);
      toastSuccess('Deleted successfully');
      fetchTabData(activeTab);
    } catch (err) {
      console.error('Delete failed', err);
      toastError('Failed to delete. Please try again.');
    }
  }

  const tabHoverStyle = {
    transition: 'color 0.2s, background 0.2s',
    cursor: 'pointer',
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="h2 mb-0 text-dark">Offer Price</h2>
        <div className="d-flex gap-3 align-items-center flex-wrap">
          {/* header actions now in tab area */}
        </div>
      </div>

<div className="d-flex justify-content-end align-items-center mb-2 gap-2">
  <button
    className="btn btn-sm btn-light"
    onClick={() => setShowFilters(s => !s)}
    style={{ height: 36 }}
  >
    <i className="fas fa-filter me-1"></i> Filter
  </button>

  <button
    className="btn btn-primary"
    onClick={handleAdd}
    style={{ fontSize: 15, borderRadius: 6 }}
  >
    + Add Price List
  </button>
</div>

{/* Row 2 → Tabs Left + Search Right */}
      <div className="d-flex justify-content-between align-items-center mb-3">

        {/* Tabs */}
        <ul className="nav nav-pills" role="tablist">
          {tabConfig.map(tab => (
            <li className="nav-item me-2" key={tab.key}>
              <button
                type="button"
                className={`nav-link px-3 py-2 ${
                  activeTab === tab.key
                    ? 'active bg-primary text-white'
                    : 'bg-light text-dark'
                }`}
                onClick={() => {
                  setActiveTab(tab.key);
                  setSearchTerm('');
                }}
                style={{ borderRadius: 6 }}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Search Box */}
        <div className="input-group input-group-sm" style={{ width: 260 }}>
          <input
            type="search"
            className="form-control"
            placeholder="Type to search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {showFilters && (
        <div className="mb-3">
          <CommanFilters
            filters={filters}
            dropdowns={{
              clients: buildOptions(filterDropdowns.clients),
              states: buildOptions(filterDropdowns.states),
              userCategories: buildOptions(filterDropdowns.userCategories),
            }}
            visibleFields={activeTab === 'client' ? ['dateRange', 'client'] : (activeTab === 'state' ? ['dateRange', 'state'] : ['dateRange', 'userCategory'])}
            onFilterChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))}
            onApplyFilters={() => fetchTabData(activeTab, filters)}
            onClearFilters={() => { setFilters({ startDate: '', endDate: '', client: '', state: '', userCategory: '' }); fetchTabData(activeTab, {}); }}
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <CommonTable
          columns={currentTab.columns}
          data={rows}
        />
      )}
    </div>
  );
}

export default ItemManage;